import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPostBySlug, getRecentPosts } from '../data/blog';
import { Dog, Calendar, Clock, ArrowLeft, ArrowRight, User, Tag, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const recentPosts = getRecentPosts(3, slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription,
    "image": post.image,
    "author": {
      "@type": "Organization",
      "name": "Dog Boarding Baltimore"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Dog Boarding Baltimore",
      "logo": {
        "@type": "ImageObject",
        "url": "https://dogboardingbaltimore.com/favicon.svg"
      }
    },
    "datePublished": post.date,
    "dateModified": post.date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://dogboardingbaltimore.com/blog/${post.slug}`
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://dogboardingbaltimore.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://dogboardingbaltimore.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `https://dogboardingbaltimore.com/blog/${post.slug}`
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.tags.join(', ')} />
        <link rel="canonical" href={`https://dogboardingbaltimore.com/blog/${post.slug}`} />
        
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:url" content={`https://dogboardingbaltimore.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={post.image} />
        <meta property="article:published_time" content={post.date} />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        <meta name="twitter:image" content={post.image} />
        
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 md:h-20">
              <Link to="/" className="flex items-center gap-2 group">
                <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center group-hover:bg-amber-600 transition-colors">
                  <Dog className="w-6 h-6 text-white" />
                </div>
                <div className="hidden sm:block">
                  <span className="font-bold text-lg text-stone-800">Dog Boarding</span>
                  <span className="block text-xs text-amber-600 font-medium -mt-1">Baltimore</span>
                </div>
              </Link>
              <div className="flex items-center gap-4">
                <Link to="/blog" className="text-stone-600 hover:text-amber-600 font-medium text-sm hidden sm:block">
                  Blog
                </Link>
                <Link to="/">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-2 rounded-lg">
                    Get Free Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="pt-20 md:pt-24 bg-stone-50 border-b border-stone-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex items-center gap-2 text-sm flex-wrap">
              <Link to="/" className="text-stone-500 hover:text-amber-600">Home</Link>
              <ChevronRight className="w-4 h-4 text-stone-300" />
              <Link to="/blog" className="text-stone-500 hover:text-amber-600">Blog</Link>
              <ChevronRight className="w-4 h-4 text-stone-300" />
              <span className="text-stone-800 font-medium line-clamp-1">{post.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Header */}
        <article>
          <header className="pt-8 pb-8 bg-stone-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="bg-amber-100 text-amber-700 text-sm font-semibold px-3 py-1 rounded-full">
                  {post.category}
                </span>
                <span className="text-stone-500 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-stone-800 mb-6 leading-tight">
                {post.title}
              </h1>
              
              <p className="text-xl text-stone-600 mb-6">
                {post.excerpt}
              </p>
              
              <div className="flex items-center gap-6 text-sm text-stone-500">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {post.author}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-12">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-[300px] md:h-[400px] object-cover rounded-2xl shadow-lg"
            />
          </div>

          {/* Article Content */}
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            <div className="prose prose-lg prose-stone max-w-none">
              {/* Introduction */}
              <div className="text-lg text-stone-700 leading-relaxed whitespace-pre-line mb-8">
                {post.content.intro}
              </div>

              {/* Sections */}
              {post.content.sections.map((section, index) => (
                <div key={index} className="mb-10">
                  <h2 className="text-2xl font-bold text-stone-800 mb-4">
                    {section.title}
                  </h2>
                  <div className="text-stone-700 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </div>
              ))}

              {/* Conclusion */}
              <div className="mt-12 p-6 bg-amber-50 rounded-xl border border-amber-100">
                <h2 className="text-2xl font-bold text-stone-800 mb-4">Final Thoughts</h2>
                <div className="text-stone-700 leading-relaxed whitespace-pre-line">
                  {post.content.conclusion}
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-stone-200">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-4 h-4 text-stone-500" />
                {post.tags.map((tag, index) => (
                  <span key={index} className="bg-stone-100 text-stone-600 text-sm px-3 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA Box */}
            <div className="mt-10 p-8 bg-amber-500 rounded-2xl text-center">
              <h3 className="text-2xl font-bold text-white mb-3">
                Need Dog Boarding in Baltimore?
              </h3>
              <p className="text-amber-100 mb-6">
                Trusted by Baltimore families for 5+ years. Background-checked staff and daily updates.
              </p>
              <Link to="/">
                <Button className="bg-white text-amber-600 hover:bg-amber-50 font-bold px-8 py-3 rounded-xl">
                  Get Your Free Quote
                </Button>
              </Link>
            </div>
          </div>
        </article>

        {/* Related Posts */}
        <section className="py-16 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-stone-800 mb-8">More Articles You Might Like</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {recentPosts.map((relatedPost) => (
                <Link key={relatedPost.slug} to={`/blog/${relatedPost.slug}`} className="group block">
                  <article className="bg-white rounded-xl overflow-hidden border border-stone-100 hover:border-amber-200 hover:shadow-lg transition-all">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <span className="text-amber-600 text-xs font-semibold">{relatedPost.category}</span>
                      <h3 className="text-lg font-bold text-stone-800 mt-2 group-hover:text-amber-600 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <span className="text-sm text-stone-500 mt-2 flex items-center gap-1">
                        Read More <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/blog">
                <Button variant="outline" className="font-semibold">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to All Articles
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-stone-900 text-stone-400 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                  <Dog className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-white">Dog Boarding Baltimore</span>
              </Link>
              <p className="text-sm">
                © {new Date().getFullYear()} Dog Boarding Baltimore. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPostPage;
